import { createContext, useContext, useState, useEffect, useMemo } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

// One axios instance shared across the app. `withCredentials: true` makes the
// browser send the httpOnly auth cookie on every request automatically — no
// more localStorage tokens.
const apiInstance = axios.create({
  baseURL: API,
  withCredentials: true,
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount, ask the backend who we are. If the cookie is valid → user;
  // otherwise the request 401s and we treat it as a clean logged-out state.
  useEffect(() => {
    let cancelled = false;
    apiInstance
      .get("/auth/me")
      .then((res) => {
        if (cancelled) return;
        setUser(res.data);
      })
      .catch(() => {
        if (cancelled) return;
        setUser(null);
        // Clear any legacy localStorage tokens from the previous Bearer-based
        // auth so the next page load doesn't try to use them.
        try { localStorage.removeItem("ameenji_token"); } catch (e) { /* ignore */ }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const login = async (email, password) => {
    const res = await apiInstance.post("/auth/login", { email, password });
    // Cookie is set by the backend automatically — we just remember the user.
    setUser(res.data.user);
    // Belt-and-braces: nuke any old token from localStorage.
    try { localStorage.removeItem("ameenji_token"); } catch (e) { /* ignore */ }
    return res.data.user;
  };

  const hasPermission = (perm) => {
    if (!user) return false;
    if (user.role === "admin") return true;
    const perms = user.permissions || [];
    return perms.includes(perm);
  };

  const logout = async () => {
    // Fire-and-forget — even if the network call fails, we want to clear local state.
    try { await apiInstance.post("/auth/logout"); } catch (e) { /* ignore */ }
    try { localStorage.removeItem("ameenji_token"); } catch (e) { /* ignore */ }
    setUser(null);
  };

  // `token` is exposed for back-compat (a couple of old components read it but
  // never actually use it for headers any more). It's intentionally null —
  // anyone consuming it can no-op without crashing.
  const value = useMemo(
    () => ({ user, token: null, loading, login, logout, api: apiInstance, hasPermission }),
    [user, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
